import * as React from 'react';
import {
    BackButton,
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallRegular,
    ChevronDownIcon,
    FruitfulCustomIcon,
    Icon,
    Separator,
    Sheet,
    View,
    XStack,
    YStack,
    type YStackProps,
} from 'smoothie';

import { SelectableRow } from '../../../../../../bills/components/common/BillRows';
import type { BankAccountDisplayDetail } from '../../../../../types/BankAccount';
import {
    type PaycheckRoutingRule,
    PaycheckRoutingRuleAccountType,
} from '../../../../../types/PaycheckRouting';
import PaycheckRoutingCard from '../../../common/PaycheckRoutingCard';

interface RuleTargetAccountProps {
    bankAccounts: BankAccountDisplayDetail[];
    rule: Partial<PaycheckRoutingRule>;
    updateTargetAccountId: (targetAccountId: string) => void;
    cardProps?: YStackProps;
    iconProps?: { iconSize: number; padding: number };
    disabled?: boolean;
    openSelect: () => void;
    isEditingRemainingRules?: boolean;
}

const RuleTargetAccount = ({
    bankAccounts,
    rule,
    updateTargetAccountId,
    cardProps,
    iconProps,
    disabled,
    openSelect,
    isEditingRemainingRules,
}: RuleTargetAccountProps) => {
    const [showTargetAccountSheet, setShowTargetAccountSheet] =
        React.useState(false);

    const targetAccount = bankAccounts.find(
        (details) => details.bankAccountId === rule.accountId,
    );

    const { color, icon, nickname } = targetAccount ?? {};

    const renderAccountCard = (onPress: () => void) => (
        <PaycheckRoutingCard onPress={onPress} {...cardProps}>
            <XStack justifyContent="space-between" alignItems="center">
                <XStack gap={12} alignItems="center">
                    {targetAccount && (
                        <FruitfulCustomIcon
                            customSize={{
                                iconSize: 16,
                                padding: 8,
                                ...iconProps,
                            }}
                            icon={icon}
                            color={color}
                        />
                    )}
                    <BodyMediumRegular
                        color={targetAccount ? '$black' : '$gray800'}
                    >
                        {nickname ?? 'Choose account'}
                    </BodyMediumRegular>
                </XStack>
                <Icon icon={ChevronDownIcon} color="$gray1000" />
            </XStack>
        </PaycheckRoutingCard>
    );

    return isEditingRemainingRules ? (
        <>
            {renderAccountCard(() => {
                if (!disabled) setShowTargetAccountSheet(true);
            })}
            <Sheet
                isOpen={showTargetAccountSheet}
                onClose={() => {
                    setShowTargetAccountSheet(false);
                }}
                showCloseButton={false}
            >
                <YStack gap={32}>
                    <XStack alignItems="center" justifyContent="space-between">
                        <BackButton
                            customBackFn={() => {
                                setShowTargetAccountSheet(false);
                            }}
                        />
                        <BodyLargeRegular>Choose account</BodyLargeRegular>
                        <View width={32} />
                    </XStack>
                    <YStack separator={<Separator />}>
                        {bankAccounts.map(
                            (
                                {
                                    icon,
                                    color,
                                    nickname,
                                    bankAccountId,
                                    accountNumberMasked,
                                },
                                i,
                            ) => (
                                <View paddingVertical={8} key={bankAccountId}>
                                    <SelectableRow
                                        isSelected={
                                            rule.accountId === bankAccountId
                                        }
                                        isMultiSelect={false}
                                        isFirstRow={i === 0}
                                        onSelect={() => {
                                            updateTargetAccountId(
                                                bankAccountId,
                                            );
                                            setShowTargetAccountSheet(false);
                                        }}
                                        rowContentComponent={
                                            <XStack
                                                alignItems="center"
                                                gap={16}
                                            >
                                                <FruitfulCustomIcon
                                                    customSize={{
                                                        iconSize: 20,
                                                        padding: 14,
                                                    }}
                                                    icon={icon}
                                                    color={color}
                                                    useAltColors={
                                                        rule.accountType ===
                                                        PaycheckRoutingRuleAccountType.INTERNAL_INVESTMENT
                                                    }
                                                />
                                                <YStack gap={4}>
                                                    <BodyMediumRegular>
                                                        {nickname}
                                                    </BodyMediumRegular>
                                                    <BodySmallRegular color="$gray1000">
                                                        Fruitful{' '}
                                                        {accountNumberMasked.slice(
                                                            -5,
                                                        )}
                                                    </BodySmallRegular>
                                                </YStack>
                                            </XStack>
                                        }
                                    />
                                </View>
                            ),
                        )}
                    </YStack>
                </YStack>
            </Sheet>
        </>
    ) : (
        <YStack gap={8}>
            {renderAccountCard(() => {
                if (!disabled) openSelect();
            })}
        </YStack>
    );
};

export default RuleTargetAccount;
